import { db, auth } from '@/firebase'
import {
  collection,
  onSnapshot,
  orderBy,
  query,
  where,
} from 'firebase/firestore'
import { getUserRoleFromProfile, resolveUserProfile } from '@/services/userProfile'

export const ACTIVE_STATUSES = [
  'Submitted', 'submitted',
  'In-Progress', 'in-progress', 'In Progress', 'in progress',
  'In-Review', 'in-review', 'In Review', 'in review',
  'Review-Done', 'review-done', 'Review Done', 'review done',
]

export const INACTIVE_STATUSES = [
  'Completed', 'completed',
  'Rejected', 'rejected',
  'Closed', 'closed',
]

const ACTIVE_STATUS_SET = new Set(ACTIVE_STATUSES.map((status) => normalizeStatus(status)))
const INACTIVE_STATUS_SET = new Set(INACTIVE_STATUSES.map((status) => normalizeStatus(status)))

export const CASE_SCOPES = Object.freeze({
  CUSTOMER_ACTIVE: 'customer-active',
  CUSTOMER_PAST: 'customer-past',
  EMPLOYEE_HOME: 'employee-home',
  EMPLOYEE_ACTIVE: 'employee-active',
  EMPLOYEE_PAST: 'employee-past',
})

export async function getUserRole(uid) {
  if (!uid) return 'User'
  const user = auth.currentUser
  const profile = await resolveUserProfile(user).catch(() => null)
  if (profile?.exists) return getUserRoleFromProfile(profile)
  return 'User'
}

export function normalizeStatus(status) {
  return String(status || '').trim().toLowerCase()
}

export function isActiveStatus(status) {
  return ACTIVE_STATUS_SET.has(normalizeStatus(status))
}

export function isInactiveStatus(status) {
  return INACTIVE_STATUS_SET.has(normalizeStatus(status))
}

export function isSubmittedStatus(status) {
  return normalizeStatus(status) === 'submitted'
}

function isNotInterestedForUser(caseItem, uid) {
  if (!uid) return false
  return Array.isArray(caseItem?.notInterestedEmployeeIds)
    && caseItem.notInterestedEmployeeIds.includes(uid)
}

export function caseMatchesScope(caseItem, { scope, uid }) {
  if (!caseItem) return false

  if (scope === CASE_SCOPES.CUSTOMER_ACTIVE) {
    return caseItem.createdBy === uid && isActiveStatus(caseItem.status)
  }

  if (scope === CASE_SCOPES.CUSTOMER_PAST) {
    return caseItem.createdBy === uid && isInactiveStatus(caseItem.status)
  }

  if (scope === CASE_SCOPES.EMPLOYEE_HOME) {
    return isSubmittedStatus(caseItem.status)
      && !caseItem.assignedToUid
      && !isNotInterestedForUser(caseItem, uid)
  }

  if (scope === CASE_SCOPES.EMPLOYEE_ACTIVE) {
    return caseItem.assignedToUid === uid && isActiveStatus(caseItem.status)
  }

  if (scope === CASE_SCOPES.EMPLOYEE_PAST) {
    return caseItem.assignedToUid === uid && isInactiveStatus(caseItem.status)
  }

  return false
}

function buildQueryForScope(scope, uid, ordered = true) {
  const constraints = []

  if (scope === CASE_SCOPES.CUSTOMER_ACTIVE || scope === CASE_SCOPES.CUSTOMER_PAST) {
    if (!uid) return null
    constraints.push(where('createdBy', '==', uid))
  }

  if (ordered) constraints.push(orderBy('updatedAt', 'desc'))

  return query(collection(db, 'cases'), ...constraints)
}

function sortByUpdatedDesc(items) {
  return [...items].sort((a, b) => {
    const ta = a.updatedAt?.toMillis ? a.updatedAt.toMillis() : (a.updatedAt?.seconds || 0) * 1000
    const tb = b.updatedAt?.toMillis ? b.updatedAt.toMillis() : (b.updatedAt?.seconds || 0) * 1000
    return tb - ta
  })
}

function mapSnapshotRows(snap) {
  return snap.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }))
}

function filterRows(rows, scope, uid) {
  return rows.filter((row) => caseMatchesScope(row, { scope, uid }))
}

export function watchCount({ scope, uid }, cb) {
  const q = buildQueryForScope(scope, uid, false)
  if (!q) {
    cb(0)
    return () => {}
  }

  const unsub = onSnapshot(
    q,
    (snap) => cb(filterRows(mapSnapshotRows(snap), scope, uid).length),
    () => cb(0)
  )

  return () => unsub()
}

export function watchList({ scope, uid }, cb) {
  const orderedQuery = buildQueryForScope(scope, uid, true)
  if (!orderedQuery) {
    cb([])
    return () => {}
  }

  let fallbackUnsub = null
  let lastItems = []

  const handleRows = (rows) => {
    const filtered = filterRows(rows, scope, uid)
    lastItems = filtered
    cb(filtered)
  }

  const primaryUnsub = onSnapshot(
    orderedQuery,
    (snap) => handleRows(mapSnapshotRows(snap)),
    (error) => {
      if (error?.code === 'failed-precondition') {
        const fallbackQuery = buildQueryForScope(scope, uid, false)
        if (!fallbackQuery) {
          cb([])
          return
        }

        fallbackUnsub = onSnapshot(
          fallbackQuery,
          (snap) => handleRows(sortByUpdatedDesc(mapSnapshotRows(snap))),
          () => cb(lastItems || [])
        )
        return
      }

      if (error?.code === 'permission-denied') {
        cb(lastItems || [])
        return
      }

      cb([])
    }
  )

  return () => {
    primaryUnsub && primaryUnsub()
    fallbackUnsub && fallbackUnsub()
  }
}
